/**
 * v5.1 Phase 52 — Per-agent pipeline metrics table.
 *
 * Sortable columns: agentId, runs, median ms, p95 ms, retry rate,
 * failure rate, dominant error category. Backend returns rows already
 * sorted by agentId ascending; this component lets the operator
 * resort by any column and click a row to drive the trend chart.
 */
import { useMemo, useState } from "react";

const COLUMNS = [
  { key: "agentId", label: "Agent", numeric: false },
  { key: "runs", label: "Runs", numeric: true },
  { key: "medianDurationMs", label: "Median (ms)", numeric: true },
  { key: "p95DurationMs", label: "p95 (ms)", numeric: true },
  { key: "retryRate", label: "Retry rate", numeric: true, percent: true },
  { key: "failureRate", label: "Failure rate", numeric: true, percent: true },
  {
    key: "dominantErrorCategory",
    label: "Dominant error",
    numeric: false,
  },
];

function formatCell(row, col) {
  const v = row[col.key];
  if (v === null || v === undefined) return "—";
  if (col.percent) return `${(v * 100).toFixed(1)}%`;
  if (col.numeric && typeof v === "number") {
    return new Intl.NumberFormat().format(Math.round(v));
  }
  return v;
}

function compare(a, b, key, direction) {
  const va = a[key];
  const vb = b[key];
  if (va === vb) return 0;
  if (va === null || va === undefined) return 1;
  if (vb === null || vb === undefined) return -1;
  if (typeof va === "number" && typeof vb === "number") {
    return direction === "asc" ? va - vb : vb - va;
  }
  const cmp = String(va).localeCompare(String(vb));
  return direction === "asc" ? cmp : -cmp;
}

export default function PipelineAgentTable({ agents = [], selectedAgentId, onSelect }) {
  const [sortKey, setSortKey] = useState("agentId");
  const [sortDir, setSortDir] = useState("asc");

  const rows = useMemo(
    () => [...agents].sort((a, b) => compare(a, b, sortKey, sortDir)),
    [agents, sortKey, sortDir],
  );

  function handleHeaderClick(key) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  if (!agents.length) {
    return (
      <p className="admin-page__hint">
        No pipeline metrics in the selected window. Pipeline runs from the
        last 7 days will populate this table.
      </p>
    );
  }

  return (
    <div className="admin-pipeline-metrics__table-wrap">
      <table className="admin-pipeline-metrics__table">
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                className={`admin-pipeline-metrics__th${col.numeric ? " admin-pipeline-metrics__th--numeric" : ""}`}
                onClick={() => handleHeaderClick(col.key)}
                aria-sort={
                  sortKey === col.key
                    ? sortDir === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                }
              >
                {col.label}
                {sortKey === col.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isSelected = row.agentId === selectedAgentId;
            return (
              <tr
                key={row.agentId}
                className={`admin-pipeline-metrics__row${isSelected ? " admin-pipeline-metrics__row--selected" : ""}`}
                onClick={() => onSelect && onSelect(row.agentId)}
              >
                {COLUMNS.map((col) => (
                  <td
                    key={col.key}
                    className={col.numeric ? "admin-pipeline-metrics__td--numeric" : ""}
                  >
                    {formatCell(row, col)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
